import { createAction } from 'redux-actions';
import {
  path,
  ifElse,
  always,
  identity,
  compose,
  prop,
  has,
  not,
  when,
  tap,
  evolve,
  converge,
  mergeRight,
  pick,
} from 'ramda';

import { Reason } from 'packages/rum';
import logError from 'app/error/logError';
import { isDeltaConfigValid } from 'app/libs/isDeltaConfigValid';
import { FETCH_DELTA_CONFIG_SUCCESS } from 'app/features/deltaConfig/actionTypes';
import {
  LANDING_PAGE,
  COMPLETE_PAGE,
} from 'app/features/deltaConfig/constants';
import {
  getOfferId,
  selectAppConfigKey,
  getCampaignUnavailablePath,
  isOnUnavailablePage,
  getCouponCampaignId,
  getAppName,
} from 'app/router/selectors';
import {
  userSessionId,
  isDirectToPay,
  userAccessType,
} from 'app/features/user/selectors';
import { referrerName } from 'app/analytics/selectors';
import { isCampaign } from 'app/features/deltaConfig/selectors';

const fetchConfigSuccess = createAction(FETCH_DELTA_CONFIG_SUCCESS);

const evolveConfig = state =>
  evolve({
    [LANDING_PAGE]: converge(mergeRight, [
      pick(['isNewExperience']),
      prop(isDirectToPay(state) ? 'directToPay' : 'freeTrial'),
    ]),
    [COMPLETE_PAGE]: prop(userAccessType(state)),
  });

const fetchDeltaConfig =
  () =>
  (dispatch, getState, { api }) =>
    api
      .fetchDeltaConfig({
        offerId: getOfferId(getState()),
        campaignId: getCouponCampaignId(getState()),
      })
      .then(
        compose(
          ifElse(isDeltaConfigValid, identity, always({})),
          path(['data', 'config_delta']),
        ),
      )
      .then(
        tap(
          compose(
            dispatch,
            fetchConfigSuccess,
            evolveConfig(getState()),
            prop(selectAppConfigKey(getState())),
          ),
        ),
      )
      .then(
        when(
          data =>
            isCampaign(getState()) &&
            not(has(selectAppConfigKey(getState()), data)),
          () => {
            throw new Error(
              `No active experiment data for the campaign "${getAppName(
                getState(),
              )}".`,
            );
          },
        ),
      )
      .catch(e => {
        if (isCampaign(getState()) && !isOnUnavailablePage(getState())) {
          window.location.assign(getCampaignUnavailablePath(getState()));
          throw e;
        } else {
          logError(e, { reason: Reason.Api });
        }
      })
      .finally(() => {
        const state = getState();
        const sessionId = userSessionId(state);
        const referrer = referrerName(state);

        return referrer && api.setMarketingReferrer({ sessionId, referrer });
      });

export { fetchDeltaConfig, evolveConfig };
